import { useState, ReactElement, useCallback } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { RemirrorContentType } from '@remirror/core'
import {
  MoreVertical,
  Search,
  PlusCircle,
  Trash2,
  Edit3,
  X,
} from 'react-feather'
import clsx from 'clsx'

import {
  Input,
  Modal,
  Dropdown,
  useShowMessage,
  useGlobalState,
  useGetTemplates,
  useDocsContent,
} from '@pepper/ui'

import { createTemplates } from '@pepper/utils/src/api/editor-templates/editor-templates'
import { deleteTemplate } from '@pepper/utils/src/api/editor/editor'
import { useSelectedCompanyId } from 'utils/functions/misc'

import { ReactComponent as TemplateIcon } from 'assets/images/template-icon.svg'
import { ReactComponent as EmptyIcon } from 'assets/images/template-empty-state.svg'

import TemplateContent from './TemplateContent'
import { ReactComponent as LoaderIcon } from 'assets/images/loader.svg'
import styles from './_.module.css'

interface Props {
  setContent: (content: RemirrorContentType) => void
  toggleAppPane?: (val: AppPane) => void
  docWordCount?: number
  isOpenFromNavbar: boolean
}

function AllTemplatesModal({
  setContent,
  toggleAppPane,
  docWordCount,
  isOpenFromNavbar = false,
}: Props) {
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateDetails | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showInsertModal, setShowInsertModal] = useState(false)

  const companyId = useSelectedCompanyId()
  const modal = useModal()
  const createContentWithTemplate = useModal('create-seo-content')
  const showMessage = useShowMessage()
  const { pepperEditorType } = useGlobalState()

  const templates = useGetTemplates({
    data: { companyId, includePepperTemplate: 1 },
  })
  const docsContent = useDocsContent({
    data: { id: selectedTemplate?.id, apiType: 'TEMPLATE' },
  })

  const createNewTemplate = useCallback(async () => {
    const { data, error, status } = await createTemplates({
      companyId,
      templateName: 'Untitled Template',
    })
    if (status === 'ok') {
      window.open(`/${companyId}/template/${data.template_id}`, '_blank')
    } else {
      showMessage({
        type: 'error',
        message: error,
      })
    }
  }, [companyId, showMessage])

  const pepperTemplates = templates.data?.filter(data => !data?.company) ?? []
  const companyTemplates = templates.data?.filter(data => data?.company) ?? []

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const filteredPepperTemplates = pepperTemplates.filter(template =>
    template.template_name.toLowerCase().includes(searchText.toLowerCase())
  )

  const filteredCompanyTemplates = companyTemplates.filter(template =>
    template.template_name.toLowerCase().includes(searchText.toLowerCase())
  )

  const openTemplate = (templateID?: string) => {
    if (!templateID) return
    window.open(`/${companyId}/template/${templateID}`)
  }

  const handleDeleteTemplate = useCallback(async () => {
    const { status } = await deleteTemplate({
      id: selectedTemplate?.id as string,
    })
    if (status === 'ok') {
      showMessage({
        type: 'success',
        message: 'Template removed successfully',
      })
      templates.refetch()
      setSelectedTemplate(null)
      setShowDeleteModal(false)
    } else {
      showMessage({
        type: 'error',
        message: 'Unable to delete template',
      })
    }
  }, [selectedTemplate?.id, showMessage, templates])

  const dropdownOverlay = (): ReactElement => {
    return (
      <div className={styles.dropdownMenu}>
        <div
          className={styles.editIconContainer}
          role="none"
          onClick={() => openTemplate(selectedTemplate?.id)}
        >
          <Edit3 size={13} className={styles.dropdownIcon} />{' '}
          <span>Edit Template</span>
        </div>
        <div
          className={styles.deleteIconContainer}
          role="none"
          onClick={() => {
            setShowDeleteModal(true)
          }}
        >
          <Trash2 size={13} className={styles.dropdownIcon} /> Delete
        </div>
      </div>
    )
  }

  return (
    <Modal
      title="Templates List"
      closable={true}
      centered={true}
      visible={modal.visible}
      className={styles.templateModal}
      onCancel={() => {
        toggleAppPane?.('ALL_APPS')
        modal.remove()
      }}
    >
      <div className={styles.templateSearchList}>
        <div className={styles.inputBoxContainer}>
          <Input
            onChange={handleChange}
            // value={searchText}
            prefix={<Search size={15} className={styles.searchIcon} />}
            placeholder="Find a Template..."
            className={styles.inputBox}
          />
        </div>
        <div className={styles.templateList}>
          {templates.isLoading && (
            <div className={styles.templateListLoaderIconContainer}>
              <LoaderIcon className={styles.loaderIcon} />
            </div>
          )}
          {filteredCompanyTemplates.length > 0 && (
            <div className={styles.companyTemplates}>
              <div className={styles.templateType}>Team Templates</div>
              {filteredCompanyTemplates.map(item => (
                <div
                  key={item.id}
                  className={clsx(styles.templateName, {
                    [styles.selectedTemplate]: selectedTemplate?.id === item.id,
                  })}
                  role="none"
                  onClick={() => setSelectedTemplate(item)}
                >
                  {item.template_name}
                  <span className={styles.templateCreatorName}>
                    {`Created by ${item.creator.first_name} ${item.creator.last_name}`}
                  </span>
                </div>
              ))}
            </div>
          )}
          {filteredPepperTemplates.length > 0 && (
            <div className={styles.pepperTemplates}>
              <div className={styles.templateType}>Pepper Templates</div>
              {filteredPepperTemplates.map(item => (
                <div
                  key={item.id}
                  className={clsx(styles.templateName, {
                    [styles.selectedTemplate]: selectedTemplate?.id === item.id,
                  })}
                  role="none"
                  onClick={() => setSelectedTemplate(item)}
                >
                  {item.template_name}
                  <span className={styles.templateCreatorName}>
                    {`Created by ${item.creator.first_name} ${item.creator.last_name}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          className={styles.createTemplateButton}
          onClick={() => {
            createNewTemplate()
            modal.remove()
          }}
        >
          <PlusCircle size={13} className={styles.plusIcon} />
          Create New Template
        </button>
      </div>
      <div className={styles.templateDisplay}>
        {selectedTemplate && (
          <div className={styles.templateHeader}>
            <div className={styles.templateTitleContainer}>
              <TemplateIcon className={styles.templateIcon} />
              <div className={styles.templateTitle}>
                {selectedTemplate?.template_name}
              </div>
              <span className={styles.line} />
              <div className={styles.templateCreator}>
                Created by{' '}
                {selectedTemplate &&
                  `${selectedTemplate.creator.first_name} ${selectedTemplate.creator.last_name}`}
              </div>
            </div>
            <div className={styles.templateCTAButtons}>
              <Dropdown
                trigger={['click']}
                overlay={dropdownOverlay}
                placement="bottomLeft"
              >
                <button className={styles.editDeleteButton}>
                  <MoreVertical size={16} />
                </button>
              </Dropdown>
              <button
                className={styles.useTemplateButton}
                onClick={() => {
                  if (!pepperEditorType || isOpenFromNavbar) {
                    createContentWithTemplate.show({
                      topics: [],
                      shouldDirectToBrief: false,
                      shouldCreatePrivateDoc: true,
                      modalType: 'project',
                      templateStorageData: {
                        templateId: selectedTemplate?.id,
                        templateName: selectedTemplate?.template_name,
                        templateCreatorName: `${selectedTemplate.creator.first_name} ${selectedTemplate.creator.last_name}`,
                        isTeamTemplate: selectedTemplate?.company
                          ? true
                          : false,
                      },
                    })
                  } else {
                    if (Number(docWordCount) > 0) {
                      setShowInsertModal(true)
                    } else {
                      setContent(
                        docsContent.data?.content.json as RemirrorContentType
                      )
                      modal.remove()
                    }
                  }
                }}
              >
                Use this Template
              </button>
              <div
                className={styles.modalCloseButton}
                role="none"
                onClick={() => modal.remove()}
              >
                <X size={14} />
              </div>
            </div>
          </div>
        )}
        {!selectedTemplate && (
          <div className={styles.templateEmptyHeader}>
            <div
              className={styles.modalCloseButton}
              role="none"
              onClick={() => modal.remove()}
            >
              <X size={14} />
            </div>
          </div>
        )}
        <div className={styles.templateContent}>
          {!selectedTemplate && (
            <div className={styles.blankCanvas}>
              <EmptyIcon className={styles.blankCanvasIcon} />
              <div className={styles.blankTextHeader}>
                Beat the blank canvas
              </div>
              <div className={styles.blankText}>
                <div>Create new Templates or use existing ones</div>
                <div>to help creators jump-start their work</div>
              </div>
            </div>
          )}
          {selectedTemplate && (
            <TemplateContent
              isLoading={docsContent.isLoading}
              content={docsContent.data?.content.json}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {showDeleteModal && (
        <Modal
          visible={showDeleteModal}
          okText="Delete"
          title="Delete Template"
          onCancel={() => {
            setShowDeleteModal(false)
          }}
          onOk={handleDeleteTemplate}
          className={styles.deleteModal}
        >
          Are you sure? You’ll not be able to use this template for any new
          project assignments.
        </Modal>
      )}
      {showInsertModal && (
        <Modal
          visible={showInsertModal}
          okText="Insert Template"
          title="Insert Template?"
          onCancel={() => {
            setShowInsertModal(false)
          }}
          onOk={() => {
            setContent(docsContent.data?.content.json as RemirrorContentType)
            setShowInsertModal(false)
            modal.remove()
          }}
          className={styles.insertModal}
        >
          You have some content written on the doc, inserting a template will
          override its content. Are you sure you want to do that?
        </Modal>
      )}
    </Modal>
  )
}

export default NiceModal.create<Props>(AllTemplatesModal)
